/**
 * BrainVisualEngine — Soft light diffusion in the head area
 *
 * Visual: Cool silver-white light with soft blue undertones, expanding clarity
 * Very faint head/shoulders outline (5-8% opacity, heavily blurred)
 * Feel: spacious, clear, calm, expansive
 *
 * Color: Silver-white → soft blue → pale lavender
 * Position: upper area (22% from top)
 * Movement: gentle outward diffusion, subtle ripple
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';

const BrainVisualEngine = ({ elapsed = 0, totalDuration = 420, isActive = false }) => {
  const [breath, setBreath] = useState(0.5);
  const [pulse, setPulse] = useState(0.5);
  const [ripple, setRipple] = useState(0);
  const [depthDrift, setDepthDrift] = useState(1);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    startRef.current = Date.now();
    const tick = () => {
      const now = Date.now() - startRef.current;
      setBreath(0.5 + 0.5 * Math.sin((now % 14000) / 14000 * Math.PI * 2 - Math.PI / 2));
      setPulse(0.5 + 0.5 * Math.sin((now % 12000) / 12000 * Math.PI * 2 - Math.PI / 2));
      setRipple((now % 8000) / 8000);
      setDepthDrift(1 + 0.01 * Math.sin((now % 18000) / 18000 * Math.PI * 2));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [isActive]);

  const phase = useMemo(() => {
    if (elapsed < 60) return 'arrival';
    if (elapsed < 180) return 'expansion';
    if (elapsed < 300) return 'clarity';
    return 'integration';
  }, [elapsed]);

  const intensity = useMemo(() => {
    if (phase === 'arrival') return (0.3 + Math.min(1, elapsed / 60) * 0.3) + breath * 0.08;
    if (phase === 'expansion') return (0.6 + ((elapsed - 60) / 120) * 0.25) + breath * 0.1;
    if (phase === 'clarity') return 0.9 + breath * 0.06;
    return 0.92 + breath * 0.04;
  }, [phase, elapsed, breath]);

  const expansion = useMemo(() => {
    if (phase === 'arrival') return 0.3 + Math.min(1, elapsed / 60) * 0.2;
    if (phase === 'expansion') return 0.5 + ((elapsed - 60) / 120) * 0.3;
    if (phase === 'clarity') return 0.8 + ((elapsed - 180) / 120) * 0.12;
    return 0.92;
  }, [phase, elapsed]);

  const cX = '50%';
  const cY = '22%';

  // Ripple rings — gentle outward diffusion
  const rippleRings = useMemo(() => {
    return [0, 1, 2].map(i => ({
      size: 60 + expansion * 80 + (ripple + i * 0.33) % 1 * 100,
      opacity: (1 - ((ripple + i * 0.33) % 1)) * 0.08 * intensity,
    }));
  }, [ripple, expansion, intensity]);

  return (
    <motion.div className="absolute inset-0 overflow-hidden" data-testid="brain-visual-engine"
      style={{ transform: `scale(${depthDrift})`, transformOrigin: `50% 22%` }}>

      {/* Deep cool darkness */}
      <div className="absolute inset-0" style={{
        background: `radial-gradient(ellipse 100% 90% at 50% 22%, rgba(18,20,28,${0.5 + intensity * 0.3}) 0%, rgba(8,6,10,1) 60%)`,
      }} />

      {/* Cool atmosphere */}
      <motion.div className="absolute inset-0 pointer-events-none"
        animate={{ opacity: 0.03 + breath * 0.04 + intensity * 0.03 }}
        transition={{ duration: 3, ease: 'easeInOut' }}
        style={{ background: `radial-gradient(ellipse 80% 65% at 50% 22%, rgba(180,195,220,0.12) 0%, transparent 55%)` }} />

      {/* Very faint head outline — abstract oval, not anatomical */}
      <div className="absolute inset-0 pointer-events-none" style={{ display: 'flex', justifyContent: 'center', paddingTop: '8vh' }}>
        <motion.svg viewBox="0 0 120 160" style={{ width: '140px', height: '180px', filter: 'blur(16px)' }}
          animate={{ opacity: 0.05 + breath * 0.02 }} transition={{ duration: 3 }}>
          <ellipse cx="60" cy="55" rx="38" ry="45" fill="none" stroke="rgba(170,185,210,0.07)" strokeWidth="1.2" />
          <path d="M 35,95 C 20,110 15,135 25,155 L 95,155 C 105,135 100,110 85,95" fill="none" stroke="rgba(170,185,210,0.04)" strokeWidth="1" />
        </motion.svg>
      </div>

      {/* Outer diffusion field */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 350 + expansion * 400 + pulse * 50 + breath * 30,
          height: 320 + expansion * 350 + pulse * 45 + breath * 25,
          opacity: 0.1 + intensity * 0.2 + pulse * 0.05,
        }}
        transition={{ duration: 2.5, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(ellipse, rgba(190,205,230,0.15) 0%, rgba(175,190,215,0.04) 40%, transparent 65%)',
          filter: 'blur(35px)',
        }} />
      </motion.div>

      {/* Ripple rings — diffusing outward */}
      {rippleRings.map((ring, i) => (
        <motion.div key={`ripple-${i}`} className="absolute pointer-events-none rounded-full"
          style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
          animate={{ width: ring.size, height: ring.size * 0.9, opacity: ring.opacity }}
          transition={{ duration: 0.5, ease: 'linear' }}>
          <div className="w-full h-full rounded-full" style={{
            border: '1px solid rgba(200,215,240,0.08)',
            background: 'transparent',
            filter: 'blur(2px)',
          }} />
        </motion.div>
      ))}

      {/* Mid glow — silver-white */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 130 + expansion * 140 + pulse * 25 + breath * 14,
          height: 120 + expansion * 125 + pulse * 20 + breath * 12,
          opacity: 0.25 + intensity * 0.42 + pulse * 0.08,
        }}
        transition={{ duration: 1.8, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: `radial-gradient(ellipse 85% 80% at 50% 48%, rgba(220,230,250,${0.6 + pulse * 0.1}) 0%, rgba(200,215,240,${0.2}) 35%, transparent 65%)`,
          filter: 'blur(5px)',
        }} />
      </motion.div>

      {/* Core — bright white clarity */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 35 + expansion * 45 + pulse * 12,
          height: 32 + expansion * 40 + pulse * 10,
          opacity: 0.45 + intensity * 0.4 + pulse * 0.1,
        }}
        transition={{ duration: 1.4, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(245,248,255,0.92) 0%, rgba(225,235,250,0.5) 35%, rgba(210,220,245,0.12) 60%, transparent 75%)',
        }} />
      </motion.div>

      {/* Soft blue undertone */}
      <motion.div className="absolute pointer-events-none"
        style={{ left: cX, top: cY, transform: 'translate(-50%, -50%)' }}
        animate={{
          width: 160 + expansion * 120 + breath * 15,
          height: 150 + expansion * 100 + breath * 12,
          opacity: 0.04 + intensity * 0.08 + pulse * 0.02,
        }}
        transition={{ duration: 3, ease: 'easeInOut' }}>
        <div className="w-full h-full rounded-full" style={{
          background: 'radial-gradient(circle, rgba(140,170,215,0.25) 0%, rgba(130,160,200,0.06) 40%, transparent 60%)',
          filter: 'blur(14px)',
        }} />
      </motion.div>

      {/* CONNECTION LAYER — clarity extends beyond head, filling the space */}
      {(phase === 'clarity' || phase === 'integration') && (
        <motion.div className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{
            opacity: phase === 'clarity'
              ? Math.min(0.3, ((elapsed - 180) / 120) * 0.3) * (0.85 + breath * 0.15)
              : 0.3 + Math.min(0.2, ((elapsed - 300) / 120) * 0.2) * (0.85 + breath * 0.15),
          }}
          transition={{ duration: 6, ease: 'easeInOut' }}>
          <div className="absolute inset-0" style={{
            background: `radial-gradient(ellipse 90% 85% at 50% 22%, rgba(210,225,250,0.16) 0%, rgba(195,210,235,0.05) 30%, rgba(180,195,220,0.02) 50%, transparent 65%)`,
          }} />
        </motion.div>
      )}

      {/* Integration */}
      {phase === 'integration' && (
        <motion.div className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse 60% 50% at 50% 22%, rgba(210,225,250,0.2) 0%, transparent 50%)` }}
          initial={{ opacity: 0 }}
          animate={{ opacity: Math.min(0.5, ((elapsed - 300) / 120) * 0.5) * (0.8 + breath * 0.2) }}
          transition={{ duration: 5 }} />
      )}

      {/* Cool particles */}
      {[...Array(8)].map((_, i) => (
        <motion.div key={`p-${i}`} className="absolute rounded-full pointer-events-none"
          style={{ width: 2 + (i % 3) * 0.6, height: 2 + (i % 3) * 0.6, background: `rgba(200,215,240,${0.06 + (i % 3) * 0.03})`,
            left: `${25 + (i * 7) % 50}%`, top: `${10 + (i * 5) % 35}%` }}
          animate={{ y: [0, -10 - (i % 3) * 4, 0], x: [0, -2 + (i % 2) * 4, 0], opacity: [0, 0.08 + intensity * 0.06, 0] }}
          transition={{ duration: 15 + (i % 3) * 4, repeat: Infinity, delay: i * 2, ease: 'easeInOut' }} />
      ))}
    </motion.div>
  );
};

export default BrainVisualEngine;
